import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Grid,
  Snackbar,
  IconButton,
} from '@mui/material';
import {
  Code as CodeIcon,
  Assignment as TaskIcon,
  Folder as FolderIcon,
  BugReport as BugIcon,
  Build as BuildIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ArrowForward as ArrowForwardIcon,
  Circle as CircleIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { AIConsole } from '../ai-console';
import DeveloperDashboardService, {
  DeveloperTask,
  DeveloperProject,
  DeveloperStats,
} from '../../services/DeveloperDashboardService';
import WebSocketService, {
  ProjectAssignedEvent,
  TaskAssignedEvent,
  StatusUpdatePostedEvent,
  ProjectStatusChangedEvent,
} from '../../services/websocketService';

/**
 * Developer Dashboard - Overview & Navigation Hub
 * VERSION 3.0 - Consolidated & Simplified
 * 
 * This is now a true overview dashboard with:
 * - Statistics cards
 * - Quick access navigation
 * - Recent activity summaries
 * - Navigation to dedicated feature pages
 */
const DeveloperDashboard: React.FC = () => {
  console.log('🏠 DeveloperDashboard MOUNTED - VERSION 3.0 (OVERVIEW ONLY) 🏠');
  
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<DeveloperTask[]>([]);
  const [projects, setProjects] = useState<DeveloperProject[]>([]);
  const [stats, setStats] = useState<DeveloperStats | null>(null);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'info' | 'warning' | 'error' } | null>(null);

  useEffect(() => {
    loadDashboardData();
    initializeWebSocket();

    return () => {
      // Cleanup WebSocket subscriptions
      if (user?.id) {
        WebSocketService.unsubscribeFromUser(user.id);
      }
    };
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await DeveloperDashboardService.getDashboardData();
      setTasks(data.tasks);
      setProjects(data.projects);
      setStats(data.stats);
    } catch (err) {
      console.error('Error loading developer dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load developer dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const initializeWebSocket = async () => {
    if (!user?.id) return;

    try {
      // Subscribe to user-specific channel for real-time notifications
      await WebSocketService.subscribeToUser(user.id);

      // Listen for project assignments
      WebSocketService.on('project.assigned', (event: ProjectAssignedEvent) => {
        console.log('🚀 Project assigned:', event);
        setNotification({
          message: `You've been assigned to project: ${event.project.name}`,
          severity: 'success',
        });
        // Refresh dashboard data
        loadDashboardData();
      });

      // Listen for task assignments
      WebSocketService.on('task.assigned', (event: TaskAssignedEvent) => {
        console.log('📋 Task assigned:', event);
        setNotification({
          message: `New task assigned: ${event.task.title}`,
          severity: 'info',
        });
        // Refresh dashboard data
        loadDashboardData();
      });

      // Listen for status updates
      WebSocketService.on('status.update.posted', (event: StatusUpdatePostedEvent) => {
        console.log('📊 Status update posted:', event);
        setNotification({
          message: `Status update from ${event.author.name} on ${event.project.name}`,
          severity: 'info',
        });
      });

      // Listen for project status changes
      WebSocketService.on('project.status.changed', (event: ProjectStatusChangedEvent) => {
        console.log('🔄 Project status changed:', event);
        setNotification({
          message: `Project "${event.project.name}" status changed to ${event.newStatus}`,
          severity: 'info',
        });
        // Refresh dashboard data
        loadDashboardData();
      });
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
      // Non-critical error, don't block dashboard functionality
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'info' | 'warning' => {
    switch (status) {
      case 'done':
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'in_review':
      case 'testing':
        return 'info';
      case 'todo':
      case 'backlog':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case 'urgent':
      case 'high':
        return 'error';
      case 'normal':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get active tasks (not done/cancelled)
  const activeTasks = tasks.filter(t => !['done', 'cancelled'].includes(t.status));
  const recentTasks = activeTasks.slice(0, 5);

  // Get active projects (in progress)
  const activeProjects = projects.filter(p => p.status === 'in_progress');

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          gap={2}
          role="status"
          aria-live="polite"
          aria-label="Loading developer dashboard"
        >
          <CircularProgress aria-label="Loading indicator" />
          <Typography>Loading developer dashboard...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          role="alert"
          aria-live="assertive"
        >
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={loadDashboardData}
          aria-label="Retry loading dashboard data"
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            <CodeIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: 40 }} />
            Developer Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.name}!
          </Typography>
        </Box>
        <Chip 
          icon={<TrendingUpIcon />} 
          label={`${stats?.completionRate || 0}% Completion Rate`} 
          color="success" 
          variant="outlined"
        />
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Active Tasks
                  </Typography>
                  <Typography variant="h4">{stats?.activeTasks || 0}</Typography>
                </Box>
                <TaskIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    My Projects
                  </Typography>
                  <Typography variant="h4">{activeProjects.length}</Typography>
                </Box>
                <FolderIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Hours This Week
                  </Typography>
                  <Typography variant="h4">{stats?.thisWeekHours || 0}</Typography>
                </Box>
                <ScheduleIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4">{stats?.completedProjects || 0}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Navigation Cards */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Quick Access
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {hasPermission('task.view') && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                  <TaskIcon color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">Task Management</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  View and manage your assigned tasks with Kanban board, filters, and detailed task views.
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Todo: {tasks.filter(t => t.status === 'todo').length}</Typography>
                    <Typography variant="body2">In Progress: {tasks.filter(t => t.status === 'in_progress').length}</Typography>
                    <Typography variant="body2">In Review: {tasks.filter(t => t.status === 'in_review').length}</Typography>
                  </Box>
                </Stack>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/dashboard/developer/tasks')}
                >
                  Manage Tasks
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {hasPermission('project.view') && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                  <FolderIcon color="info" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">My Projects</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Access project details, specifications, documentation, and track project progress.
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {activeProjects.length > 0 ? (
                    <Typography variant="body2">
                      {activeProjects.length} active project{activeProjects.length !== 1 ? 's' : ''}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No active projects
                    </Typography>
                  )}
                </Stack>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/dashboard/developer/projects')}
                >
                  View Projects
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {hasPermission('code.review') && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                  <BugIcon color="error" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">Bug Tracking</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Track and manage bugs, issues, and technical debt across your projects.
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {tasks.filter(t => t.type === 'bug').length} bug{tasks.filter(t => t.type === 'bug').length !== 1 ? 's' : ''} to address
                  </Typography>
                </Stack>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/dashboard/developer/bugs')}
                >
                  View Bugs
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {hasPermission('system.access') && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                  <BuildIcon color="action" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">Development Tools</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Access development tools, documentation, and resources for your workflow.
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/dashboard/developer/tools')}
                >
                  Open Tools
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        {hasPermission('task.view') && recentTasks.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Tasks
                </Typography>
                <List dense>
                  {recentTasks.map((task, index) => (
                    <React.Fragment key={task.id}>
                      <ListItem>
                        <ListItemIcon>
                          <CircleIcon sx={{ fontSize: 8, color: `${getStatusColor(task.status)}.main` }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={task.title}
                          secondary={
                            <Box display="flex" gap={1} alignItems="center">
                              <Chip 
                                label={task.status.replace('_', ' ')} 
                                size="small" 
                                color={getStatusColor(task.status)} 
                              />
                              <Chip 
                                label={task.priority} 
                                size="small" 
                                color={getPriorityColor(task.priority)} 
                              />
                              {task.project && (
                                <Typography variant="caption" color="text.secondary">
                                  {task.project}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recentTasks.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => navigate('/dashboard/developer/tasks')}
                >
                  View All Tasks
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {hasPermission('project.view') && projects.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  My Projects
                </Typography>
                <List dense>
                  {projects.slice(0, 5).map((project, index) => (
                    <React.Fragment key={project.id}>
                      <ListItem>
                        <ListItemIcon>
                          <FolderIcon color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary={project.name}
                          secondary={
                            <Box display="flex" gap={1} alignItems="center">
                              <Chip 
                                label={project.status.replace('_', ' ')} 
                                size="small" 
                                color={getStatusColor(project.status)} 
                              />
                              <Typography variant="caption" color="text.secondary">
                                {project.progress}% complete
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < Math.min(projects.length, 5) - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => navigate('/dashboard/developer/projects')}
                >
                  View All Projects
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* AI Console Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          AI Assistant
        </Typography>
        <Card>
          <CardContent>
            <Box sx={{ height: '400px' }}>
              <AIConsole defaultContextType="technical" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Real-time Notifications */}
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.severity || 'info'}
          variant="filled"
          icon={<NotificationsIcon />}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseNotification}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DeveloperDashboard;
